import { useContext, useCallback, useEffect, useState } from 'react';
import { AuthContext } from './authContext';
import { useGlobalData } from './globalDataContext';
import { PinnerProData, userService } from '../services';

/**
 * Hook consolidato per accedere a tutti i dati utente
 * Combina AuthContext e GlobalDataContext per un accesso unificato
 */
export function useUserData() {
  const authContext = useContext(AuthContext);
  const globalData = useGlobalData();

  return {
    // Dati autenticazione
    isAuthenticated: authContext.isAuthenticated,
    authToken: authContext.authToken,
    userId: authContext.userId,
    userProfile: authContext.userProfile,
    isPinnerPro: authContext.userProfile?.is_pinner_pro_pro || false,
    isLoadingAuth: authContext.isLoading,

    // Crediti
    credits: globalData.credits,
    isLoadingCredits: globalData.isLoadingCredits,

    // Notifiche
    notifications: globalData.notifications,
    unreadCount: globalData.unreadCount,

    // Funzioni
    login: authContext.login,
    logout: authContext.logout,
    refreshProfile: authContext.refreshUserProfile,
    updateProfile: authContext.updateUserProfile,
    refreshCredits: globalData.refreshCredits,
    updateCredits: globalData.updateCredits,
    notifyCreditsUpdated: globalData.notifyCreditsUpdated
  };
}

/**
 * Hook per gestire solo i crediti con logica di business aggiuntiva
 */
export function useCredits() {
  const { credits, isLoadingCredits, refreshCredits, updateCredits } = useGlobalData();
  const [hasEnoughCredits, setHasEnoughCredits] = useState<boolean>(true);

  const checkCredits = useCallback((requiredCredits: number): boolean => {
    return credits >= requiredCredits;
  }, [credits]);

  const spendCredits = useCallback(async (amount: number): Promise<boolean> => {
    if (!checkCredits(amount)) {
      return false;
    }

    // Aggiorna ottimisticamente
    updateCredits(credits - amount);

    // Poi ricarica dal server per conferma
    setTimeout(() => refreshCredits(true), 1000);

    return true;
  }, [credits, checkCredits, updateCredits, refreshCredits]);

  useEffect(() => {
    setHasEnoughCredits(credits > 0);
  }, [credits]);

  return {
    credits,
    hasEnoughCredits,
    isLoading: isLoadingCredits,
    checkCredits,
    spendCredits,
    refresh: () => refreshCredits(true)
  };
}

/**
 * Hook per gestire un profilo utente specifico (non l'utente autenticato)
 */
export function useUserProfile(userId: string | undefined) {
  const [profile, setProfile] = useState<PinnerProData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async (forceRefresh = false) => {
    if (!userId) {
      setProfile(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await userService.getUserProfile(userId, forceRefresh);
      setProfile(data);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento del profilo');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
  }, [userId, fetchProfile]); // Ricarica quando cambia userId

  return {
    profile,
    loading,
    error,
    refresh: () => fetchProfile(true)
  };
}

/**
 * Hook per gestire le notifiche con funzionalità aggiuntive
 */
export function useNotifications() {
  const {
    notifications,
    unreadCount,
    isLoadingNotifications,
    refreshNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead
  } = useGlobalData();

  const [filterType, setFilterType] = useState<string | null>(null);
  const [showOnlyUnread, setShowOnlyUnread] = useState<boolean>(false);

  const filteredNotifications = notifications.filter(notification => {
    if (showOnlyUnread && notification.read) return false;
    return true;
  });

  return {
    notifications: filteredNotifications,
    allNotifications: notifications,
    unreadCount,
    isLoading: isLoadingNotifications,
    filterType,
    setFilterType,
    showOnlyUnread,
    setShowOnlyUnread,
    markAsRead: markNotificationAsRead,
    markAllAsRead: markAllNotificationsAsRead,
    refresh: refreshNotifications
  };
}

/**
 * Hook per verificare permessi e ruoli utente
 */
export function useUserPermissions() {
  const { userProfile } = useUserData();

  const isPinnerPro = userProfile?.is_pinner_pro_pro || false;
  const canCreatePins = !!userProfile;
  const canAccessChat = !!userProfile;
  const canBuyConsultancy = isPinnerPro;

  const checkPermission = useCallback((permission: string): boolean => {
    switch (permission) {
      case 'create_pins':
        return canCreatePins;
      case 'access_chat':
        return canAccessChat;
      case 'buy_consultancy':
        return canBuyConsultancy;
      case 'pinner_pro':
        return isPinnerPro;
      default:
        return false;
    }
  }, [canCreatePins, canAccessChat, canBuyConsultancy, isPinnerPro]);

  return {
    isPinnerPro,
    canCreatePins,
    canAccessChat,
    canBuyConsultancy,
    checkPermission
  };
}
